import { Box, Flex, Link, useToast } from "@chakra-ui/react";
import { Link as RouterLink } from "react-router";

import { MarkdownDisplay } from "@/common/components/MarkdownDisplay";
import { MemberPicker } from "@/common/components/MemberPicker";
import { SectionHeading } from "@/common/components/SectionHeading";
import {
  SidebarContainer,
  SidebarSection,
} from "@/common/components/SidebarLayout";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { ArchivingSection } from "@/feature/components/ArchivingSection";
import EnvironmentActivity from "@/feature/components/EnvironmentActivity";
import { FeatureIntegrationsActions } from "@/feature/components/FeatureIntegrationsActions";
import { useFeatureUpdateMutation } from "@/feature/data/useFeatureUpdateMutation";
import { useOrgMembers } from "@/global-settings/data/useOrgMembers";
import { useFlag } from "@reflag/react-sdk";
import { FeatureSettingsUrl } from "@reflag/shared/urls";

import { useFeatureData } from "../data/useFeatureData";
import { FeatureSidebarStage } from "./FeatureSidebarStage";
import FeatureViewsSelector from "./FeatureViewsSelector";

type Props = {
  featureId?: string;
};

export default function FeatureSidebar({ featureId }: Props) {
  const { env } = useEnvironment();
  const { data: feature, isLoading } = useFeatureData(featureId);
  const { isEnabled: archivingEnabled } = useFlag("archiving");
  const updateMutation = useFeatureUpdateMutation();
  const { org } = useEnvironment();
  const { data: teamMembers = [], isLoading: isLoadingTeamMembers } =
    useOrgMembers(org.id);

  const toast = useToast();

  return (
    <SidebarContainer>
      {feature && (
        <SidebarSection gap={1}>
          <SectionHeading variant="subtle">Description</SectionHeading>
          {feature.description?.length ? (
            <Box fontSize="sm">
              <MarkdownDisplay value={feature?.description} />
            </Box>
          ) : (
            <Flex align="center" fontSize="sm" minH={7}>
              <Link
                _hover={{ textDecoration: "underline" }}
                as={RouterLink}
                color="dimmed"
                to={FeatureSettingsUrl(env, feature)}
              >
                Add a description
              </Link>
            </Flex>
          )}
        </SidebarSection>
      )}

      <SidebarSection gap={1}>
        <SectionHeading isLoading={isLoading} variant="subtle">
          Stage
        </SectionHeading>
        {feature && <FeatureSidebarStage feature={feature} />}
      </SidebarSection>

      <SidebarSection gap={1}>
        <SectionHeading isLoading={isLoading} variant="subtle">
          Owner
        </SectionHeading>
        {feature && (
          <MemberPicker
            disabled={isLoadingTeamMembers}
            members={teamMembers}
            selectedMember={feature.owner}
            onClear={() => {
              updateMutation.mutate(
                {
                  id: feature.id,
                  ownerUserId: null,
                },
                {
                  onSuccess: () => {
                    toast({
                      title: "Flag owner cleared",
                      status: "success",
                    });
                  },
                },
              );
            }}
            onSelect={(memberId) => {
              updateMutation.mutate(
                {
                  id: feature.id,
                  ownerUserId: memberId,
                },
                {
                  onSuccess: () => {
                    toast({
                      title: "Flag owner changed",
                      status: "success",
                    });
                  },
                },
              );
            }}
          />
        )}
      </SidebarSection>

      {archivingEnabled && <ArchivingSection />}

      <SidebarSection>
        <EnvironmentActivity featureId={featureId} />
      </SidebarSection>

      <SidebarSection mr={-2}>
        <FeatureViewsSelector featureId={featureId} />
      </SidebarSection>

      {feature && <FeatureIntegrationsActions feature={feature} />}
    </SidebarContainer>
  );
}
